import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import LineFloatingButton from '../../components/feature/LineFloatingButton';
import { useSEO } from '../../utils/seo';

export default function FurniturePage() {
  const navigate = useNavigate();

  useSEO({
    title: '長崎家具・家電運搬サービス | みなとサービス - 大型家具も安心',
    description: '長崎県の家具・家電運搬サービス。ソファ、ベッド、冷蔵庫など大型家具・家電の配送から設置まで対応。経験豊富なスタッフが傷をつけずに丁寧に運びます。貨物保険完備で安心。お見積もり無料。',
    keywords: '長崎,家具運搬,家電配送,大型家具,冷蔵庫配送,ソファ運搬,ベッド配送,家具移動,長崎県,配送サービス',
    canonical: '/furniture',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: '家具・家電運搬サービス',
      description: '長崎県の家具・家電運搬サービス。大型家具や家電製品の配送から設置まで対応。経験豊富なスタッフが傷をつけずに丁寧に運びます。',
      provider: {
        '@type': 'LocalBusiness',
        name: 'みなとサービス',
        telephone: '0120-743-643',
        address: {
          '@type': 'PostalAddress',
          addressRegion: '長崎県',
          addressCountry: 'JP'
        }
      },
      areaServed: {
        '@type': 'Place',
        name: '長崎県'
      },
      offers: {
        '@type': 'Offer',
        priceSpecification: {
          '@type': 'PriceSpecification',
          price: '9000',
          priceCurrency: 'JPY',
          description: '基本料金（3点まで）'
        }
      }
    }
  });

  const services = [
    {
      title: '大型家具の運搬',
      description: 'ソファ、ベッド、タンス、食器棚など、大型家具の運搬・設置を承ります。',
      icon: 'ri-sofa-fill',
      image: 'https://static.readdy.ai/image/db812a398b3a5f225f48907587ff778a/391e9cd69c64e83cc383dd5feb6e6021.jpeg'
    },
    {
      title: '家電の配送・設置',
      description: '冷蔵庫や洗濯機などの家電製品の配送から設置まで対応します。',
      icon: 'ri-fridge-fill',
      image: 'https://public.readdy.ai/ai/img_res/e5688c355f50e0de09beb37240bf8d52.jpg'
    },
    {
      title: '模様替えサポート',
      description: '室内での家具移動や配置換えもお任せください。重い家具も安全に移動します。',
      icon: 'ri-home-4-fill',
      image: 'https://static.readdy.ai/image/db812a398b3a5f225f48907587ff778a/61b51ad5178302ad807712455ecb2bf8.jpeg'
    },
    {
      title: '組立・解体サービス',
      description: '家具の組立や解体作業も承ります。組立家具もお任せください。',
      icon: 'ri-tools-fill',
      image: 'https://readdy.ai/api/search-image?query=warm%20illustration%20of%20furniture%20assembly%20tools%20and%20flat%20pack%20furniture%20parts%20neatly%20organized%2C%20screwdriver%20wrench%20and%20hardware%20components%2C%20soft%20beige%20and%20yellow%20color%20palette%2C%20minimalist%20flat%20design%20illustration%2C%20clean%20workspace%20showing%20furniture%20assembly%20concept%2C%20simple%20shapes%20and%20welcoming%20atmosphere&width=600&height=400&seq=furniture-illustration-304&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[400px] sm:h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20illustration%20of%20large%20furniture%20items%20including%20sofa%20bed%20wardrobe%20refrigerator%20and%20washing%20machine%20arranged%20in%20cozy%20room%20setting%2C%20soft%20beige%20cream%20and%20yellow%20color%20palette%2C%20minimalist%20flat%20design%20style%2C%20clean%20organized%20composition%20showing%20furniture%20and%20appliance%20delivery%20concept%2C%20gentle%20shadows%20and%20simple%20geometric%20shapes%2C%20welcoming%20atmosphere%20without%20vehicles%20or%20people&width=1920&height=600&seq=furniture-hero-illustration-501&orientation=landscape"
            alt="家具・家電運搬サービス"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-7xl font-bold mb-3 md:mb-6">
            家具・家電運搬サービス
          </h1>
          <p className="text-sm sm:text-base md:text-xl lg:text-2xl mb-3 md:mb-4 text-white/90 leading-relaxed">
            重い家具や大型家電の運搬は、<br className="sm:hidden" />
            プロにお任せください。<br className="hidden sm:block" />
            傷をつけずに丁寧に運びます
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 mb-5 md:mb-8 inline-block border border-white/30">
            <p className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-white flex items-center gap-2">
              <i className="ri-map-pin-fill text-[#ebe960] text-lg md:text-xl"></i>
              長崎県内・全国各地への<br className="sm:hidden" />
              配送に対応
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            <Link
              to="/contact"
              className="bg-[#ebe960] text-gray-900 px-6 md:px-10 py-3 md:py-4 rounded-full font-bold text-sm md:text-lg hover:bg-[#d4d34d] transition-all hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-mail-line text-lg md:text-xl"></i>
              お問い合わせはこちら
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              対応サービス
            </h2>
            <p className="text-sm md:text-lg lg:text-xl text-gray-600">
              あらゆる家具・家電の運搬に対応します
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-[#ede9e3] rounded-2xl md:rounded-3xl overflow-hidden group cursor-pointer"
              >
                <div className="w-full h-40 sm:h-48 md:h-64">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 md:p-8">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-[#ebe960] rounded-full flex items-center justify-center mb-3 md:mb-4">
                    <i className={`${service.icon} text-xl md:text-3xl text-gray-900`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-2 md:mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-700 text-xs sm:text-sm md:text-base leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-[#ede9e3]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              料金の目安
            </h2>
          </div>

          <div className="bg-white rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 lg:p-12">
            <div className="bg-[#ebe960] rounded-xl md:rounded-2xl p-3 md:p-4 mb-6 md:mb-8 text-center">
              <p className="text-gray-900 font-bold text-sm md:text-base lg:text-lg">
                基本料金
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
              <div>
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-4 md:mb-6">家具・家電の運搬</h3>
                <ul className="space-y-3 md:space-y-4">
                  {[
                    { item: '3点まで', price: '¥9,000〜' },
                    { item: '3点以上', price: '¥14,000〜' }
                  ].map((item, index) => (
                    <li key={index} className="flex justify-between items-center pb-3 md:pb-4 border-b border-gray-200">
                      <span className="text-gray-700 text-sm md:text-base">{item.item}</span>
                      <span className="text-base md:text-lg lg:text-xl font-bold text-gray-900">{item.price}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-4 md:mb-6">家具の移動など</h3>
                <ul className="space-y-3 md:space-y-4">
                  {[
                    { item: '3点まで', price: '¥6,000〜' },
                    { item: '3点以上', price: '¥10,000〜' }
                  ].map((item, index) => (
                    <li key={index} className="flex justify-between items-center pb-3 md:pb-4 border-b border-gray-200">
                      <span className="text-gray-700 text-sm md:text-base">{item.item}</span>
                      <span className="text-base md:text-lg lg:text-xl font-bold text-gray-900">{item.price}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 md:mt-8 p-4 md:p-6 bg-[#ede9e3] rounded-xl md:rounded-2xl">
              <div className="text-gray-700 text-xs md:text-sm lg:text-base leading-relaxed space-y-4 md:space-y-5">
                <p className="mb-3">
                  <strong>※料金について:</strong>
                </p>
                <p className="mb-3">
                  上記は料金の目安です。<br className="hidden sm:block" />
                  作業員の増員や階段作業、エレベーターなし、<br className="sm:hidden" />
                  狭い通路などの条件により追加料金が発生する場合があります。
                </p>
                <p>
                  また、エリアや時期(3月・4月)によって料金が変動することがございます。<br />
                  詳しくはお見積もり時にご説明いたします。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              安心のポイント
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-8">
            {[
              {
                icon: 'ri-shield-check-fill',
                title: '貨物保険完備',
                description: '万が一の事故に備え、貨物保険に加入しています。'
              },
              {
                icon: 'ri-shield-check-fill',
                title: '傷つけない技術',
                description: '専用の養生材を使用し、家具や壁を傷つけずに運搬します。'
              },
              {
                icon: 'ri-team-fill',
                title: '経験豊富なスタッフ',
                description: '重量物の運搬に慣れたプロのスタッフが対応します。'
              },
              {
                icon: 'ri-calendar-check-fill',
                title: '柔軟な日程調整',
                description: '土日祝日も対応可能。お客様のご都合に合わせます。'
              }
            ].map((point, index) => (
              <div key={index} className="bg-[#ede9e3] rounded-xl md:rounded-3xl p-4 md:p-8 text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-[#ebe960] rounded-full flex items-center justify-center mx-auto mb-3 md:mb-6">
                  <i className={`${point.icon} text-xl sm:text-2xl md:text-4xl text-gray-900`}></i>
                </div>
                <h3 className="text-sm sm:text-base md:text-xl lg:text-2xl font-bold text-gray-900 mb-2 md:mb-4">
                  {point.title}
                </h3>
                <p className="text-gray-700 text-[10px] sm:text-xs md:text-sm lg:text-base leading-relaxed">
                  {point.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-[#ede9e3]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
            お見積もり・ご相談無料
          </h2>
          <p className="text-sm md:text-lg lg:text-xl text-gray-700 mb-6 md:mb-8">
            まずはお気軽にご相談ください。
          </p>
          <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 mb-6 md:mb-8 inline-block">
            <p className="text-sm md:text-base lg:text-lg font-bold text-gray-900 flex items-center gap-2 md:gap-3">
              <i className="ri-map-2-fill text-[#ebe960] text-xl md:text-2xl"></i>
              長崎県内・全国各地への<br className="sm:hidden" />
              家具・家電配送に対応
            </p>
            <p className="text-xs md:text-sm lg:text-base text-gray-600 mt-2">
              遠方への配送もお任せください。<br className="sm:hidden" />
              詳しくはお問い合わせください。
            </p>
          </div>

          <div className="flex justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 md:gap-3 bg-gray-900 text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-full text-base sm:text-lg md:text-xl font-bold hover:bg-gray-800 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-fill text-lg sm:text-xl md:text-2xl"></i>
              お問い合わせはこちら
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <LineFloatingButton />
    </div>
  );
}
